import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUser, clearAuth, getAuthToken } from '../utils/auth';
import { getUserAssessmentsAuth } from '../utils/assessmentApi';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuth, setIsAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [showAssessmentDropdown, setShowAssessmentDropdown] = useState(false);
  const [selectedStructureType, setSelectedStructureType] = useState('');
  const [showExpertiseModal, setShowExpertiseModal] = useState(false);
  const [selectedExpertise, setSelectedExpertise] = useState('');
  const [expertiseOther, setExpertiseOther] = useState('');
  const closeTimeoutRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAdvancedDropdown, setShowAdvancedDropdown] = useState(false);
  const advancedCloseTimeoutRef = useRef(null);
  const [hasBasicAssessment, setHasBasicAssessment] = useState(false);

  // Check if on a basic assessment page
  const isOnAssessmentPage =
    location.pathname === '/building-assessment' ||
    location.pathname === '/building-assessment-intro' ||
    location.pathname === '/tunnel-assessment' ||
    location.pathname === '/bridge-assessment-intro';

  // Check if on an advanced assessment page
  const isOnAdvancedPage = location.pathname.startsWith('/advanced-');

  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setIsAuth(authStatus);
      if (authStatus) {
        const userData = getUser();
        setUser(userData);
      } else {
        setUser(null);
        setHasBasicAssessment(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    window.addEventListener('storage', checkAuth);
    window.addEventListener('authChange', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', checkAuth);
    };
  }, []);

  // Check if user has completed at least one basic assessment
  useEffect(() => {
    if (!isAuth) { setHasBasicAssessment(false); return; }
    const token = getAuthToken();
    if (!token) return;
    getUserAssessmentsAuth(token)
      .then(data => {
        const count = data?.assessments?.length || 0;
        setHasBasicAssessment(count > 0);
      })
      .catch(() => setHasBasicAssessment(false));
  }, [isAuth, location.pathname]);

  const handleHomeClick = () => {
    window.location.href = 'https://www.spplindia.org';
  };

  const handleMainWebsiteClick = () => {
    navigate('/');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleLogout = () => {
    clearAuth();
    setIsAuth(false);
    setUser(null);
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };

  const handleProfileClick = () => {
    navigate('/manage-profile');
  };

  const getUserInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  const handleStructureTypeSelect = (type) => {
    setSelectedStructureType(type);
  };

  const handleProceed = () => {
    if (!selectedStructureType) {
      alert('Please select a structure type');
      return;
    }

    // Check authentication before proceeding
    if (!isAuth) {
      alert('Please login to start assessment');
      setShowAssessmentDropdown(false);
      // Store assessment preferences for after login
      sessionStorage.setItem('pendingAssessment', JSON.stringify({
        structureType: selectedStructureType
      }));
      navigate('/login', {
        state: {
          from: '/building-assessment',
          pendingAssessment: true
        }
      });
      return;
    }

    setShowAssessmentDropdown(false);
    setShowExpertiseModal(true);
  };

  const handleExpertiseSubmit = () => {
    const finalExpertise = selectedExpertise === 'Others (Please Specify)'
      ? expertiseOther
      : selectedExpertise;

    if (!finalExpertise.trim()) {
      alert('Please select or enter the nature of expertise required');
      return;
    }

    // Save user details to sessionStorage for assessment submission
    if (user) {
      const userDetailsForAssessment = {
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        phone: user.phone || '',
        organization: user.organisation || '',
        structureType: selectedStructureType,
        q1: finalExpertise,
        q1Other: selectedExpertise === 'Others (Please Specify)' ? expertiseOther : ''
      };
      sessionStorage.setItem('oshmUserDetails', JSON.stringify(userDetailsForAssessment));
    }

    setShowExpertiseModal(false);
    // Navigate to appropriate assessment page based on structure type
    // Building/Bridge → intro landing page first; Tunnel → direct questionnaire
    const assessmentPath = selectedStructureType === 'Tunnel' ? '/tunnel-assessment' :
      selectedStructureType === 'Bridge' ? '/bridge-assessment-intro' :
        '/building-assessment-intro';
    navigate(assessmentPath, {
      state: {
        structureType: selectedStructureType,
        natureOfExpertise: finalExpertise
      }
    });
    // Reset
    setSelectedStructureType('');
    setSelectedExpertise('');
    setExpertiseOther('');
  };

  // Hover handlers for dropdown
  const openDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setShowAssessmentDropdown(true);
  };

  const closeDropdownWithDelay = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowAssessmentDropdown(false);
    }, 200);
  };

  const closeDropdownImmediate = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setShowAssessmentDropdown(false);
  };

  // Hover handlers for advanced dropdown
  const openAdvancedDropdown = () => {
    if (advancedCloseTimeoutRef.current) {
      clearTimeout(advancedCloseTimeoutRef.current);
      advancedCloseTimeoutRef.current = null;
    }
    setShowAdvancedDropdown(true);
  };

  const closeAdvancedDropdownWithDelay = () => {
    advancedCloseTimeoutRef.current = setTimeout(() => {
      setShowAdvancedDropdown(false);
    }, 200);
  };

  const closeAdvancedDropdownImmediate = () => {
    if (advancedCloseTimeoutRef.current) {
      clearTimeout(advancedCloseTimeoutRef.current);
      advancedCloseTimeoutRef.current = null;
    }
    setShowAdvancedDropdown(false);
  };

  const handleAdvancedRCCClick = () => {
    if (!isAuth) {
      alert('Please login to access advanced questionnaire');
      navigate('/login');
      return;
    }
    closeAdvancedDropdownImmediate();
    navigate('/advanced-rcc-building');
  };

  const handleAdvancedLBClick = () => {
    if (!isAuth) {
      alert('Please login to access advanced questionnaire');
      navigate('/login');
      return;
    }
    closeAdvancedDropdownImmediate();
    navigate('/advanced-lb-building');
  };

  const handleAdvancedSteelClick = () => {
    if (!isAuth) {
      alert('Please login to access advanced questionnaire');
      navigate('/login');
      return;
    }
    closeAdvancedDropdownImmediate();
    navigate('/advanced-steel-building');
  };

  const handleAdvancedCompositeClick = () => {
    if (!isAuth) {
      alert('Please login to access advanced questionnaire');
      navigate('/login');
      return;
    }
    closeAdvancedDropdownImmediate();
    navigate('/advanced-composite-building');
  };

  const handleAdvancedHeritageClick = () => {
    if (!isAuth) {
      alert('Please login to access advanced questionnaire');
      navigate('/login');
      return;
    }
    closeAdvancedDropdownImmediate();
    navigate('/advanced-heritage-building');
  };

  const structureTypes = [
    { value: 'Building', label: 'Building', disabled: false },
    { value: 'Bridge', label: 'Bridge', disabled: false },
    { value: 'Tunnel', label: 'Tunnel (Coming Soon)', disabled: true },
    { value: 'Tower', label: 'Steel Tower (In Development)', disabled: true },
    { value: 'Other', label: 'Other (In Development)', disabled: true }
  ];

  const expertiseOptions = [
    {
      value: 'Condition Assessment of Building Materials',
      label: 'Condition Assessment of Building Materials',
      explanation: 'We check the health of your building materials - like checking if the concrete is strong enough, if there are cracks or if the materials are aging.'
    },
    {
      value: 'Vibration-Based SHM',
      label: 'Vibration-Based SHM',
      explanation: 'We measure how your structure vibrates naturally. Changes in vibration patterns can tell us if something is wrong, even before visible damage appears.'
    },
    {
      value: 'NDE (Non-Destructive Evaluation)',
      label: 'NDE (Non-Destructive Evaluation)',
      explanation: 'We use special tools to look inside your structure without breaking anything - like an X-ray for buildings.'
    },
    {
      value: 'Static & Dynamic Testing',
      label: 'Static & Dynamic Testing',
      explanation: 'We apply controlled loads (weights) to your structure and measure how it responds.'
    },
    {
      value: 'Others (Please Specify)',
      label: 'Others (Please Specify)',
      explanation: 'If you have a specific monitoring need not listed above, please describe it here.'
    }
  ];

  return (
    <>
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo and Company Name */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {/* Logo */}
              <div className="flex-shrink-0">
                <img
                  src="/logo/sppl.png"
                  alt="SPPL Logo"
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%234F46E5" width="100" height="100"/%3E%3Ctext x="50" y="55" font-size="40" fill="white" text-anchor="middle" font-family="Arial"%3ESP%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>

              {/* Company Name */}
              <div className="flex flex-col -mt-1">
                {isAuth ? (
                  <h1 className="text-base sm:text-2xl lg:text-3xl font-extrabold leading-tight whitespace-nowrap" style={{ color: '#003366' }}>
                    SPPL India
                  </h1>
                ) : (
                  <h1 className="text-base sm:text-2xl lg:text-3xl font-extrabold leading-tight whitespace-nowrap" style={{ color: '#003366' }}>
                    Sanrachna Prahari Pvt Ltd
                  </h1>
                )}
                <p className="text-[11px] sm:text-xs font-semibold text-red-800 leading-tight -mt-1 whitespace-nowrap">
                  An IIT Delhi Company
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1.5 xl:space-x-2.5 2xl:space-x-4">
              {/* Home Button */}
              <button
                onClick={handleHomeClick}
                className="px-2.5 xl:px-3.5 py-2 text-xs xl:text-sm font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md transition-colors whitespace-nowrap"
              >
                Home
              </button>

              {/* Main Website Button */}
              <button
                onClick={handleMainWebsiteClick}
                className="px-2.5 xl:px-3.5 py-2 text-xs xl:text-sm font-medium text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors border border-indigo-300 whitespace-nowrap"
              >
                OSHMAS
              </button>

              {/* Demo Assessment Button */}
              {isAuth && (
                <button
                  onClick={() => navigate('/demo-assessment')}
                  className="px-3 xl:px-3.5 py-2 text-xs xl:text-sm font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-md transition-all shadow-sm flex items-center space-x-1 whitespace-nowrap cursor-pointer"
                  title="Try Free Demo Assessment"
                >
                  <span>⚡ Demo</span>
                </button>
              )}

              {/* Basic Questionnaire Dropdown */}
              {isAuth && !isOnAssessmentPage && !isOnAdvancedPage && (
                <div
                  className="relative assessment-dropdown"
                  onMouseEnter={openDropdown}
                  onMouseLeave={closeDropdownWithDelay}
                >
                  <button
                    className="px-3 xl:px-4 py-2 xl:py-2.5 text-xs xl:text-sm font-semibold text-white bg-blue-900 hover:bg-blue-800 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center space-x-1.5 cursor-pointer whitespace-nowrap"
                    title="Start your assessment"
                  >
                    <span>Basic Questionnaire</span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showAssessmentDropdown && (
                    <div
                      className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 pointer-events-auto"
                      onMouseEnter={openDropdown}
                      onMouseLeave={closeDropdownWithDelay}
                    >
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Structure Type:</h3>
                        <div className="space-y-2 mb-4">
                          {structureTypes.map((type) => (
                            <label
                              key={type.value}
                              onClick={() => { if (!type.disabled) handleStructureTypeSelect(type.value); }}
                              className={`flex items-center p-2 rounded transition-all ${type.disabled
                                ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                : 'hover:bg-gray-50 cursor-pointer'
                                }`}
                            >
                              <input
                                type="radio"
                                name="structureType"
                                value={type.value}
                                checked={selectedStructureType === type.value}
                                onChange={(e) => handleStructureTypeSelect(e.target.value)}
                                disabled={type.disabled}
                                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              />
                              <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleProceed}
                            disabled={!selectedStructureType}
                            className="flex-1 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-md hover:bg-blue-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer"
                          >
                            Proceed
                          </button>
                          <button
                            onClick={() => {
                              closeDropdownImmediate();
                              setSelectedStructureType('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Advanced Questionnaire Dropdown */}
              {isAuth && hasBasicAssessment && !isOnAssessmentPage && !isOnAdvancedPage && (
                <div
                  className="relative advanced-dropdown"
                  onMouseEnter={openAdvancedDropdown}
                  onMouseLeave={closeAdvancedDropdownWithDelay}
                >
                  <button
                    className="px-3 xl:px-4 py-2 xl:py-2.5 text-xs xl:text-sm font-semibold text-white bg-green-700 hover:bg-green-600 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center space-x-1.5 cursor-pointer whitespace-nowrap"
                    title="Advanced Questionnaire"
                  >
                    <span>Advanced Questionnaire</span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Advanced Dropdown Menu */}
                  {showAdvancedDropdown && (
                    <div
                      className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 pointer-events-auto"
                      onMouseEnter={openAdvancedDropdown}
                      onMouseLeave={closeAdvancedDropdownWithDelay}
                    >
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Assessment Type:</h3>
                        <div className="space-y-2">
                          <button
                            onClick={handleAdvancedRCCClick}
                            className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                          >
                            RCC Building
                          </button>
                          <button
                            onClick={handleAdvancedSteelClick}
                            className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                          >
                            Steel Building
                          </button>
                          <button
                            onClick={handleAdvancedCompositeClick}
                            className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                          >
                            Composite Building
                          </button>
                          <button
                            onClick={handleAdvancedLBClick}
                            className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                          >
                            Load Bearing Masonry
                          </button>
                          <button
                            onClick={handleAdvancedHeritageClick}
                            className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                          >
                            Heritage Building
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assessment Reports Button */}
              {isAuth && !isOnAssessmentPage && (
                <button
                  onClick={() => navigate('/user-reports')}
                  className="px-2.5 xl:px-3.5 py-2 text-xs xl:text-sm font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50 rounded-md transition-colors whitespace-nowrap"
                >
                  Assessment Reports
                </button>
              )}

              {/* Profile Avatar and Login/Logout */}
              {isAuth && user ? (
                <div className="flex items-center space-x-2 xl:space-x-2.5 ml-1 xl:ml-2">
                  <button
                    onClick={handleProfileClick}
                    className="w-8 h-8 xl:w-9 xl:h-9 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-xs xl:text-sm hover:from-blue-800 hover:to-blue-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                    title="Manage Profile"
                  >
                    {getUserInitials(user.firstName, user.lastName)}
                  </button>

                  <button
                    onClick={handleLogout}
                    className="px-2.5 xl:px-3.5 py-1.5 xl:py-2 text-xs xl:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm hover:shadow-md whitespace-nowrap"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLoginClick}
                  className="px-3 xl:px-4 py-1.5 xl:py-2 text-xs xl:text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 rounded-md transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Login
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center space-x-2">
              {/* Mobile Profile Avatar */}
              {isAuth && user && (
                <button
                  onClick={handleProfileClick}
                  className="w-10 h-10 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-xs hover:from-blue-800 hover:to-blue-600 transition-all shadow-lg"
                  title="Profile"
                >
                  {getUserInitials(user.firstName, user.lastName)}
                </button>
              )}

              {/* Hamburger Menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 min-w-[44px] min-h-[44px] rounded-md text-gray-700 hover:text-indigo-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {!mobileMenuOpen ? (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <button
                  onClick={() => { handleHomeClick(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50"
                >
                  Home
                </button>

                <button
                  onClick={() => { handleMainWebsiteClick(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50 border border-indigo-300"
                >
                  OSHMAS
                </button>

                {isAuth && (
                  <button
                    onClick={() => { navigate('/demo-assessment'); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300"
                  >
                    ⚡ Demo Assessment
                  </button>
                )}

                {isAuth && !isOnAssessmentPage && !isOnAdvancedPage && (
                  <button
                    onClick={() => {
                      setShowAssessmentDropdown(true);
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-blue-900 hover:bg-blue-800"
                  >
                    Basic Questionnaire
                  </button>
                )}

                {isAuth && hasBasicAssessment && !isOnAssessmentPage && !isOnAdvancedPage && (
                  <button
                    onClick={() => {
                      setShowAdvancedDropdown(true);
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-green-700 hover:bg-green-600"
                  >
                    Advanced Questionnaire
                  </button>
                )}

                {isAuth && !isOnAssessmentPage && (
                  <button
                    onClick={() => { navigate('/user-reports'); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-gray-50"
                  >
                    Assessment Reports
                  </button>
                )}

                {isAuth ? (
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={() => { handleLoginClick(); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-blue-900 hover:bg-blue-800"
                  >
                    Login
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Add spacing for fixed Navbar */}
      <div className="h-16 lg:h-20"></div>

      {/* Mobile Assessment Dropdown Modal */}
      {showAssessmentDropdown && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3">Select Structure Type:</h3>
              <div className="space-y-2 mb-4">
                {structureTypes.map((type) => (
                  <label
                    key={type.value}
                    onClick={() => { if (!type.disabled) handleStructureTypeSelect(type.value); }}
                    className={`flex items-center p-2 sm:p-3 rounded transition-all ${type.disabled
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                  >
                    <input
                      type="radio"
                      name="structureType"
                      value={type.value}
                      checked={selectedStructureType === type.value}
                      onChange={(e) => handleStructureTypeSelect(e.target.value)}
                      disabled={type.disabled}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  onClick={handleProceed}
                  disabled={!selectedStructureType}
                  className="flex-1 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-md hover:bg-blue-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Proceed
                </button>
                <button
                  onClick={() => {
                    closeDropdownImmediate();
                    setSelectedStructureType('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expertise Modal */}
      {showExpertiseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Nature of Expertise Required</h2>
              <p className="text-xs sm:text-sm text-gray-600 mb-4">
                Please select the type of expertise or assessment you need for your {selectedStructureType.toLowerCase()}.
              </p>

              <div className="space-y-2 sm:space-y-3 mb-4">
                {expertiseOptions.map((option) => {
                  const isSelected = selectedExpertise === option.value;
                  return (
                    <label
                      key={option.value}
                      onClick={() => setSelectedExpertise(option.value)}
                      className={`flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all cursor-pointer ${isSelected
                        ? 'border-blue-900 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                    >
                      <input
                        type="radio"
                        name="expertiseSelection"
                        value={option.value}
                        checked={isSelected}
                        onChange={(e) => setSelectedExpertise(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex-shrink-0 mt-1">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-blue-900 bg-blue-900' : 'border-gray-300 bg-white'
                          }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-800 mb-1">{option.label}</div>
                        <div className="text-xs text-gray-600">{option.explanation}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {selectedExpertise === 'Others (Please Specify)' && (
                <textarea
                  value={expertiseOther}
                  onChange={(e) => setExpertiseOther(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Please specify your expertise requirements..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900 resize-none mb-4 text-sm sm:text-base"
                  rows={3}
                />
              )}

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4">
                <button
                  onClick={handleExpertiseSubmit}
                  className="flex-1 px-4 py-2 sm:py-3 bg-blue-900 text-white font-medium rounded-lg hover:bg-blue-800 transition-colors shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  Submit
                </button>
                <button
                  onClick={() => {
                    setShowExpertiseModal(false);
                    setSelectedExpertise('');
                    setExpertiseOther('');
                  }}
                  className="px-4 py-2 sm:py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Advanced Questionnaire Modal */}
      {showAdvancedDropdown && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3">Select Assessment Type:</h3>
              <div className="space-y-2 mb-4">
                <button
                  onClick={handleAdvancedRCCClick}
                  className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                >
                  RCC Building
                </button>
                <button
                  onClick={handleAdvancedLBClick}
                  className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                >
                  Load Bearing Masonry
                </button>
                <button
                  onClick={handleAdvancedSteelClick}
                  className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                >
                  Steel Building
                </button>
                <button
                  onClick={handleAdvancedCompositeClick}
                  className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                >
                  Composite Building
                </button>
                <button
                  onClick={handleAdvancedHeritageClick}
                  className="w-full text-left px-4 py-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg transition-all text-sm font-medium text-gray-700 hover:text-green-700"
                >
                  Heritage Building
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => closeAdvancedDropdownImmediate()}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
